import { SceneManager } from "../../common/scene.manager";
import { accuracyEnum } from '../../common/models/accuracy.enum';
import { sectionType } from "../../sectionType";
import { Amenity } from "./models/amenity";
import { Section } from "./models/section";
import { Unit } from "./models/unit";
import { PartTransformation } from "./models/part-transformation";
import { InstancedMesh } from "@babylonjs/core";
import { unitTypeEnum } from "../../unit-type-enum";
import { amenityTypeEnum } from "../../amenity-type-enum";
import { Point3 } from "../../common/models/point.type";
import { ColumnDefinition } from "./models/ColumnDefinition/column-definition";

export class BaseBabylonService {

    public sceneManager: SceneManager;
    public basePathUnits = '/assets/units/';
    public basePathAmenities = '/assets/amenities/';
    public section;

    constructor(sceneManager: SceneManager) {
        this.sceneManager = sceneManager;
    }

    setSection(section) {
        this.section = section;
    }

    getAmenity(amenityType: string, offsetBasedOnColumn: number, height: number, groupId: string, subgroupId: string, locationInSite: any, partTransformation: any, accuracy: accuracyEnum, floorHeightOfBuilding: number): Amenity {
        const amenityDescription = this.section.amenities.find(amenity => amenity.amenityType === amenityType) as Amenity;
        amenityDescription.origin = [0, 0, 0];
        this.loadAmenity(amenityDescription, offsetBasedOnColumn, height, groupId, subgroupId, locationInSite, partTransformation, accuracy, floorHeightOfBuilding);
        return amenityDescription;
    }

    renderInstances(groupId: string | number, subgroupId: string, ua: { units?: Unit[], amenities?: Amenity[], variant?: number }[], locationInSite, partTransformation: PartTransformation, accuracy: accuracyEnum, floorHeightOfBuilding: number, variant?: number) {
        const promises = [];
        if (ua && ua.length > 0) {
            ua.forEach(u => {
                const promisesUnitInstance = this.renderUnitInstance(groupId, subgroupId, u.units, locationInSite, partTransformation, accuracy, floorHeightOfBuilding, variant);
                const promisesAmenityInstance = this.renderAmenityInstance(groupId, subgroupId, u.amenities, locationInSite, partTransformation, accuracy, floorHeightOfBuilding);
                promises.push(...promisesUnitInstance, ...promisesAmenityInstance);
            });
        }
        return promises;
    }

    getUnitAndAmenityData(s: sectionType, offsetBasedOnColumn: number, height: number): { units?: Unit[], amenities?: Amenity[] } {
        let units: Unit[] = [];
        const sectionDescription = this.section.sections.find(sec => sec.sectionType === s) as Section;
        if (sectionDescription && sectionDescription.units && sectionDescription.units.length > 0) {
            units = this.getUnitData(sectionDescription, offsetBasedOnColumn, height);
        }
        let amenities: Amenity[] = [];
        if (sectionDescription && sectionDescription.amenities && sectionDescription.amenities.length > 0) {
            amenities = this.getAmenityData(sectionDescription, offsetBasedOnColumn, height);
        }
        return { units, amenities };
    }

    getFileNameAndRemainder(path: string) {
        const pathParts = path.split('/');
        const fileName = pathParts.pop();
        const remainder = pathParts[1];
        return { fileName, remainder };
    }

    migrateToSegments(c) {
        if (c) {
            c.segments.forEach(segment => {
                if (segment['sectionType'] && !segment.sectionTypes) {
                    segment.sectionTypes = [segment['sectionType']];
                };
            })
        }
    }

    private getUnitData(sectionDescription: Section, offsetBasedOnColumn: number, height: number): Unit[] {
        const unitsForSection = sectionDescription.units.map(unit => {
            return { ...this.section.units.find(u => u.unitType === unit.unitType), origin: unit.origin, rotZ: unit.rotZ ? unit.rotZ : 0, offsetBasedOnColumn, height } as Unit;
        });
        return unitsForSection;
    }

    private renderUnitInstance(groupId: string | number, subgroupId: string, unitsForSection: Unit[], locationInSite, partTransformation, accuracy: accuracyEnum, floorHeightOfBuilding: number, variant?: number): Promise<InstancedMesh>[] {
        const promises = [];

        if (unitsForSection && unitsForSection.length > 0) {
            unitsForSection.forEach(unit => {
                if (unit.floors) {
                    unit.floors.forEach((f, index) => {
                        const locationInBuilding: Point3 = [
                            unit.origin[0] + f.origin[0] + unit.offsetBasedOnColumn,
                            unit.origin[2] + f.origin[2] + unit.height,
                            -(unit.origin[1] + f.origin[1]),
                        ];
                        unit.locationInBuilding = locationInBuilding;
                        unit.floorInBuilding = (unit.origin[2] + unit.height) / floorHeightOfBuilding;
                        unit.floorInSection = index;
                        if (unit && unit.unitType !== undefined) {
                            const promise = this.sceneManager.modelLoaderService.getInstanceMesh(this.modelOnAccuracy(accuracy, index, unit.unitType, variant), this.basePathUnits,
                                unit.locationInBuilding,
                                { groupId, subgroupId, type: 'building', props: {} },
                                unit.rotZ,
                                locationInSite,
                                partTransformation,
                                accuracy,
                                unit.color,
                                true);
                            promises.push(promise);
                        }
                    });
                }
            });
        }
        return promises;
    }

    private getAmenityData(sectionDescription: Section, offsetBasedOnColumn: number, height: number): Amenity[] {
        let amenitiesForSection = sectionDescription.amenities.map(amenity => {
            return { ...this.section.amenities.find(u => u.amenityType === amenity.amenityType), origin: amenity.origin, rotZ: amenity.rotZ ? amenity.rotZ : 0, offsetBasedOnColumn, height };
        });
        return amenitiesForSection;
    }


    private renderAmenityInstance(groupId: string | number, subgroupId: string, amenitiesForSection: Amenity[], locationInSite, sectionTransformation, accuracy: accuracyEnum, floorHeightOfBuilding: number): Amenity[] {
        const promises = [];
        if (amenitiesForSection && amenitiesForSection.length > 0) {
            amenitiesForSection.forEach(amenity => {
                const promisesLoad = this.loadAmenity(amenity, amenity.offsetBasedOnColumn, amenity.height, groupId, subgroupId, locationInSite, sectionTransformation, accuracy, floorHeightOfBuilding);
                promises.push(...promisesLoad);
            });
        }
        return promises;
    }


    private loadAmenity(amenity: Amenity, offsetBasedOnColumn: number, height: number, groupId: string | number, subgroupId: string, locationInSite: any, sectionTransformation: any, accuracy: accuracyEnum, floorHeightOfBuilding: number): Promise<InstancedMesh>[] {
        const promises = [];
        if (amenity.floors) {
            amenity.floors.forEach((f, index) => {
                const locationInBuilding: Point3 = [
                    amenity.origin[0] + f.origin[0] + offsetBasedOnColumn,
                    amenity.origin[2] + f.origin[2] + height,
                    -(amenity.origin[1] + f.origin[1]),
                ];
                amenity.locationInBuilding = locationInBuilding;
                amenity.floorInBuilding = (amenity.origin[2] + height) / floorHeightOfBuilding;
                amenity.floorInSection = index;
                if (amenity && amenity.amenityType !== undefined) {
                    const promise = this.sceneManager.modelLoaderService.getInstanceMesh(this.modelOnAccuracy(accuracy, index, amenity.amenityType), this.basePathAmenities,
                        amenity.locationInBuilding,
                        { groupId, subgroupId, type: 'building', props: {} },
                        amenity.rotZ,
                        locationInSite,
                        sectionTransformation,
                        accuracy,
                        amenity.color);
                    promises.push(promise);
                }
            });
        }
        return promises;
    }


    private modelOnAccuracy(accuracy, floor, type: unitTypeEnum | amenityTypeEnum, variant?: number) {
        let result = `${type}-F${floor}-${accuracy}.glb`;
        return result;
    }

}